import { Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Textarea } from '@/components/ui/Textarea'
import { formatDateTime } from '@/utils/format'
import { useSubmit } from '../api'
import type { Assignment, Submission } from '../types'
import { AttachmentField, type Attached } from './AttachmentField'
import { useFieldErrors } from './useFieldErrors'

/** The student hands in (or replaces) their work: a written answer and/or files. */
export function SubmitDialog({
  studentId,
  assignment,
  submission,
  onOpenChange,
}: {
  studentId: number
  assignment: Assignment
  submission: Submission | null
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('lms')
  const submit = useSubmit(studentId)
  const [body, setBody] = useState(submission?.body ?? '')
  const [files, setFiles] = useState<Attached[]>(
    submission?.files.map((f) => ({ id: f.id, name: f.name, size: f.size })) ?? [],
  )
  const { errors, formError, reset, apply } = useFieldErrors(['body', 'fileIds'] as const)
  const late = new Date(assignment.dueAt) < new Date()

  const send = async () => {
    reset()
    try {
      await submit.mutateAsync({
        assignmentId: assignment.id,
        body: body.trim() || null,
        fileIds: files.map((f) => f.id),
      })
      toast.success(t('Work handed in'), assignment.title)
      onOpenChange(false)
    } catch (e) {
      apply(e)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="lg"
      icon={<Send />}
      title={submission ? t('Change my work') : t('Hand in my work')}
      description={t('{{title}} · due {{date}}', {
        title: assignment.title,
        date: formatDateTime(assignment.dueAt),
      })}
      dismissible={!submit.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={submit.isPending}>
            {t('Cancel')}
          </Button>
          <Button leftIcon={<Send />} onClick={() => void send()} loading={submit.isPending}>
            {t('Hand in')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {late ? (
          <Alert tone="warning" title={t('The due date has passed')}>
            {t('Your work will be marked late.')}
          </Alert>
        ) : null}
        {formError ? (
          <Alert tone="danger" title={t('Your work was not handed in')}>
            {formError}
          </Alert>
        ) : null}
        <FormField label={t('My answer')} error={errors.body}>
          <Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
        </FormField>
        <FormField label={t('Files')} error={errors.fileIds}>
          <AttachmentField value={files} onChange={setFiles} disabled={submit.isPending} />
        </FormField>
        {submission ? (
          <p className="text-xs text-muted">
            {t('Last handed in {{date}}. You can change it until your teacher grades it.', {
              date: formatDateTime(submission.submittedAt),
            })}
          </p>
        ) : null}
      </div>
    </Dialog>
  )
}
